<script setup lang="ts">
import { amountRegex } from "~/constants/regex.constants";

const emit = defineEmits(["close", "editComplete"]);
defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
});

const { t } = useI18n();
const { trackEvent } = useGAnalytics();
const { marketplaceFeesPercent } = useRuntimeConfig().public;
const isLoading = ref(false);
const errorMessageAmount = ref("");
const listingPrice = defineModel("editedListingPrice", { type: String });
const displayFees = ref(true);

const listingPriceAfterFee = computed(() => {
  if (!Number(marketplaceFeesPercent)) {
    handleFeesError();
    return "";
  } else if (validateInput()) {
    return (
      Number(listingPrice.value) *
      (1 - Number(marketplaceFeesPercent) / 100)
    ).toFixed(2);
  } else {
    return 0;
  }
});

const feePercentageDisplay = computed(() => {
  if (!Number(marketplaceFeesPercent)) {
    handleFeesError();
    return "";
  } else {
    return Number(marketplaceFeesPercent);
  }
});

const handleFeesError = () => {
  if (!Number(marketplaceFeesPercent)) {
    displayFees.value = false;
    errorMessageAmount.value = t("errorMessages.default");
  }
};

const validateInput = () => {
  errorMessageAmount.value = "";
  if (!listingPrice.value || Number(listingPrice.value) < 0.01) {
    errorMessageAmount.value = t("errorMessages.transferInvalidAmount");
    return false;
  } else if (!amountRegex.test(listingPrice.value.toString())) {
    errorMessageAmount.value = t("errorMessages.listingInvalidCharacters");
    return false;
  } else if (isOverLimit(listingPrice.value)) {
    errorMessageAmount.value = t("errorMessages.transferInvalidAmount");
    return false;
  } else {
    errorMessageAmount.value = "";
    return true;
  }
};

const submitEditedListing = () => {
  trackEvent("edit_listing_button");
  if (!validateInput()) return;
  isLoading.value = true;
  setTimeout(() => {
    emit("editComplete");
  }, 500);
};
</script>
<template>
  <form class="flex flex-col gap-y-6" @submit.prevent="submitEditedListing">
    <div class="flex gap-4 pb-2">
      <img
        class="w-16 object-contain border border-secondary-500"
        :src="getPortraitItemImage(collectible.imageId)"
        @error="
          (e) => ((e.target as HTMLImageElement).src = getPortraitItemImage())
        " />
      <div class="flex flex-col justify-center gap-1">
        <i18n-t
          tag="p"
          class="text-neutral-300"
          keypath="main.modalList.editInfo"
          scope="global">
          <template #item>
            <span class="font-bold text-neutral-200">{{
              $t(`${collectible.translationId}.title`) +
              " " +
              $t(`${collectible.translationId}.subtitle`) +
              " #" +
              collectible.number
            }}</span>
          </template>
        </i18n-t>
      </div>
    </div>
    <div>
      <p class="text-xs font-bold text-neutral-300">
        {{ $t("main.modalList.editPrice") }}
      </p>
      <BaseInputGFAL v-model:amount="listingPrice" />
    </div>
    <i18n-t
      v-show="displayFees"
      tag="p"
      class="text-neutral-300 text-xs w-fit"
      keypath="main.modalList.feeInfo"
      scope="global">
      <template #price>
        <span class="font-bold text-neutral-200"
          >{{ listingPriceAfterFee }} GFAL</span
        >
      </template>
      <template #fee>
        <span class="text-xs text-neutral-200 font-bold">
          {{ $t("main.modalList.fee", { fee: feePercentageDisplay }) }}
        </span>
      </template>
    </i18n-t>
    <p class="text-error-300 text-xs h-4 -my-4">
      {{ errorMessageAmount }}
    </p>
    <div class="w-full mt-auto mb-2 flex items-center gap-4">
      <BaseButtonSubmit
        :loading="isLoading"
        :text="$t('main.modalList.confirmChanges')"
        :disabled="isLoading || errorMessageAmount !== '' || !listingPrice"
        data-test="button-confirm-edit-listing"
        :contextual-styles="true" />
      <BaseButtonCancel
        :text="$t('main.modalList.cancel')"
        data-test="button-dismiss-edit-listing"
        :contextual-styles="true"
        @click="$emit('close')" />
    </div>
  </form>
</template>
